import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n/helpers";
import { SITE_URL } from "@/i18n/seoConfig";
import { preserveEleone } from "@/utils/preserveEleone";

export const CONTACT_EMAIL = "contact@nanda-sa.com";

export const Section = ({ children, className = "", testId }) => (
 <section data-testid={testId} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[880px] px-6 md:px-10 py-16 md:py-24">{children}</div>
 </section>
);
export const H2 = ({ children }) => (
 <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-nanda-ink mb-8">{children}</h2>
);
export const H3 = ({ children }) => (
 <h3 className="font-serif text-xl md:text-2xl text-nanda-ink mt-10 mb-4">{children}</h3>
);
export const P = ({ children }) => (
 <p className="text-nanda-inkSoft text-[16px] md:text-[17px] leading-[1.8] mb-5">{children}</p>
);
export const UL = ({ items }) => (
 <ul className="space-y-2 mb-6 text-nanda-inkSoft text-[16px] md:text-[17px] leading-[1.75]">
 {items.map((it, i) => (
 <li key={i} className="flex items-baseline gap-3">
 <span className="h-px w-4 bg-nanda-terracotta shrink-0 mt-3" />
 <span>{it}</span>
 </li>
 ))}
 </ul>
);
export const KPI = ({ n, l, d }) => (
 <div className="border-t border-nanda-line pt-6">
 <div className="font-serif text-5xl md:text-6xl text-nanda-terracotta leading-none">{n}</div>
 <div className="label-overline text-nanda-ink mt-4">{l}</div>
 <p className="text-nanda-muted text-sm mt-2 leading-snug">{d}</p>
 </div>
);
export const InlineCTA = ({ to, eyebrow, title, description, cta, testId }) => (
 <div data-testid={testId} className="my-10 border-l-2 border-nanda-terracotta bg-nanda-sand/30 pl-6 pr-4 py-6">
 <div className="label-overline text-nanda-terracotta mb-2">{preserveEleone(eyebrow)}</div>
 <div className="font-serif text-lg md:text-xl text-nanda-ink mb-2 leading-snug">{title}</div>
 <p className="text-nanda-inkSoft text-[15px] leading-[1.7] mb-4">{description}</p>
 <Link
 to={to}
 className="inline-flex items-center gap-2 label-overline text-nanda-ink border-b border-nanda-ink pb-0.5 hover:text-nanda-terracotta hover:border-nanda-terracotta transition-colors duration-300"
 >
 {preserveEleone(cta)}
 <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </div>
);

/* ────────────── SEO ────────────── */
const injectJsonLd = (data, tag) => {
 const s = document.createElement("script");
 s.setAttribute("type", "application/ld+json");
 s.setAttribute("data-seo", tag);
 s.textContent = JSON.stringify(data);
 document.head.appendChild(s);
 return s;
};

export function useSectorSeo({ tag, path, title, description, image, locale, breadcrumb, keywords }) {
 React.useEffect(() => {
 document.title = title;
 const setMeta = (attr, key, value) => {
 let el = document.head.querySelector(`meta[${attr}="${key}"]`);
 if (!el) {
 el = document.createElement("meta");
 el.setAttribute(attr, key);
 document.head.appendChild(el);
 }
 el.setAttribute("content", value);
 };
 setMeta("name", "description", description);
 setMeta("property", "og:title", title);
 setMeta("property", "og:description", description);
 setMeta("property", "og:image", `${SITE_URL}${image}`);
 setMeta("property", "og:type", "article");
 setMeta("name", "twitter:title", title);
 setMeta("name", "twitter:description", description);

 injectJsonLd(
 {
 "@context": "https://schema.org",
 "@type": "Article",
 headline: title,
 description,
 image: `${SITE_URL}${image}`,
 author: { "@type": "Organization", name: "NANDA SÁ" },
 publisher: { "@type": "Organization", name: "NANDA SÁ", logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.svg` } },
 datePublished: "2026-02-15",
 dateModified: "2026-02-15",
 inLanguage: locale === "en" ? "en-GB" : locale === "pt" ? "pt-PT" : "fr-FR",
 mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
 keywords,
 },
 tag
 );
 injectJsonLd(
 {
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: locale === "en" ? "Home" : locale === "pt" ? "Início" : "Accueil", item: `${SITE_URL}/` },
 { "@type": "ListItem", position: 2, name: locale === "en" ? "Sectors" : locale === "pt" ? "Sectores" : "Secteurs", item: `${SITE_URL}/secteurs` },
 { "@type": "ListItem", position: 3, name: breadcrumb, item: `${SITE_URL}${path}` },
 ],
 },
 `${tag}-bc`
 );
 return () => {
 document.head.querySelectorAll(`script[data-seo="${tag}"]`).forEach((el) => el.remove);
 document.head.querySelectorAll(`script[data-seo="${tag}-bc"]`).forEach((el) => el.remove);
 };
 }, [tag, path, title, description, image, locale, breadcrumb, keywords]);
}

/* ────────────── Passerelles CTA partagées ────────────── */
export const CTAs = {
 NandaSa: () => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-nanda-sa"
 to="/marques/nanda-sa"
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "NANDA SÁ. La cajou de Guinée-Bissau, transformée avec exigence.",
 "NANDA SÁ. Cashew from Guinea-Bissau, processed with rigour.",
 "NANDA SÁ. O caju da Guiné-Bissau, transformado com exigência."
 )}
 description={tt(
 "Notre maison épicerie fine incarne la stratégie de transformation locale : torréfaction, aromatisation, conditionnement premium.",
 "Our fine-food house embodies the local processing strategy: roasting, flavouring, premium packaging.",
 "A nossa casa de mercearia fina encarna a estratégia de transformação local: torrefacção, aromatização, embalagem premium."
 )}
 cta={tt("Découvrir NANDA SÁ", "Discover NANDA SÁ", "Descobrir NANDA SÁ")}
 />
 );
 },
 Energia: () => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-energiapro"
 to="/marques/energia-pro"
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "NANDA SÁ Energia Pro. Solaire résidentiel, professionnel et rural.",
 "NANDA SÁ Energia Pro. Residential, professional and rural solar.",
 "NANDA SÁ Energia Pro. Solar residencial, profissional e rural."
 )}
 description={tt(
 "Panneaux Q.SAVE-G3, batteries MARSTEK Venus A, mini-réseaux, pompage solaire : les solutions énergétiques concrètes.",
 "Q.SAVE-G3 panels, MARSTEK Venus A batteries, mini-grids, solar pumping: concrete energy solutions.",
 "Painéis Q.SAVE-G3, baterias MARSTEK Venus A, mini-redes, bombagem solar: soluções energéticas concretas."
 )}
 cta={tt("Découvrir Energia Pro", "Discover Energia Pro", "Descobrir a Energia Pro")}
 />
 );
 },
 Eleone: () => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-cosmetiques"
 to="/cosmetiques"
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "NANDA SÁ Cosmétiques. La cosmétique botanique ouest-africaine.",
 "NANDA SÁ Cosmétiques. West African botanical cosmetics.",
 "NANDA SÁ Cosmétiques. A cosmética botânica oeste-africana."
 )}
 description={tt(
 "Huile de coque de cajou, karité, plantes de la région : nous transformons ces ressources en soins de haute exigence.",
 "Cashew nut shell oil, shea, plants of the region: we transform these resources into high-standard skincare.",
 "Óleo da casca do caju, karité, plantas da região: transformamos estes recursos em cosméticos de exigência."
 )}
 cta={tt("Découvrir NANDA SÁ Cosmétiques", "Discover NANDA SÁ Cosmétiques", "Descobrir NANDA SÁ Cosmétiques")}
 />
 );
 },
 Conseil: () => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-conseil"
 to="/conseil-implantation"
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "Conseil en implantation. De l'idée au terrain.",
 "Establishment advisory. From idea to ground.",
 "Consultoria de implantação. Da ideia ao terreno."
 )}
 description={tt(
 "Étude de marché, foncier, démarches administratives, recrutement, partenariats locaux : nous accompagnons les investisseurs et les diasporas.",
 "Market study, land, administrative procedures, recruitment, local partnerships: we support investors and diasporas.",
 "Estudo de mercado, imobiliário, formalidades, recrutamento, parcerias locais: acompanhamos investidores e diásporas."
 )}
 cta={tt("Découvrir le Conseil en implantation", "Discover the Establishment Advisory", "Descobrir a Consultoria de implantação")}
 />
 );
 },
 Contact: ({ subject }) => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-contact"
 to={`/contact?subject=${encodeURIComponent(subject || "Opportunités Guinée-Bissau")}`}
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "Parlons de votre projet.",
 "Let's talk about your project.",
 "Falemos do seu projecto."
 )}
 description={tt(
 "Investisseur, entreprise, ONG, institution, diaspora : partagez vos intentions, nous répondons personnellement.",
 "Investor, company, NGO, institution, diaspora: share your intentions, we respond personally.",
 "Investidor, empresa, ONG, instituição, diáspora: partilhe as suas intenções, respondemos pessoalmente."
 )}
 cta={tt("Contacter NANDA SÁ", "Contact NANDA SÁ", "Contactar a NANDA SÁ")}
 />
 );
 },
};

/* ────────────── Hero commun ────────────── */
export const SectorHero = ({ heroImg, breadcrumb, title, subtitle, alt }) => (
 <section className="relative h-[70vh] min-h-[500px] w-full overflow-hidden bg-nanda-ink">
 <img src={heroImg} alt={alt} className="absolute inset-0 h-full w-full object-cover" loading="eager" />
 <div className="absolute inset-0 bg-black/45" />
 <div className="absolute inset-0 bg-gradient-to-r from-nanda-ink/70 via-nanda-ink/30 to-transparent" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-16 md:pb-24">
 <div className="label-overline text-white/80 mb-6">
 <Link to="/secteurs" className="hover:text-white transition-colors">{breadcrumb.parent}</Link>
 <span className="mx-3 opacity-50">·</span>
 <span>{breadcrumb.current}</span>
 </div>
 <h1 className="font-serif text-white text-5xl sm:text-6xl md:text-7xl lg:text-[84px] leading-[1.02] max-w-4xl">
 {title}
 </h1>
 <p className="mt-8 max-w-2xl text-white/90 text-lg md:text-xl leading-relaxed">{subtitle}</p>
 </div>
 </section>
);

/* ────────────── Conclusion commune ────────────── */
export const ConclusionBlock = ({ quote, subject, primaryLink = "/opportunites", primaryLabel }) => {
 const tt = useT();
 return (
 <>
 <p className="font-serif text-2xl md:text-3xl italic text-nanda-ink mt-8">{quote}</p>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link
 to={primaryLink}
 className="btn-arrow inline-flex items-center gap-3 bg-nanda-terracotta text-white px-7 py-4 hover:bg-nanda-terracotta transition-colors duration-500 label-overline"
 >
 {primaryLabel || tt("Le panorama complet des opportunités", "The full opportunities panorama", "O panorama completo das oportunidades")}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <Link
 to={`/contact?subject=${encodeURIComponent(subject)}`}
 className="btn-arrow inline-flex items-center gap-3 bg-transparent border border-nanda-ink text-nanda-ink px-7 py-4 hover:bg-nanda-ink hover:text-white transition-colors duration-500 label-overline"
 >
 {tt("Nous écrire :", "Write to us :", "Escreva-nos :")} {CONTACT_EMAIL}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </>
 );
};
